import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { formatMoney } from '../utils/paymentLinks.utils';

interface SummaryCard {
  key: string;
  label: string;
  count: number;
  amount: number;
  color: string;
  icon: React.ReactNode;
  tooltip: string;
}

interface SummaryCardsProps {
  activeKey?: string;
  groups: SummaryCard[];
  onSelect?: (key: string) => void;
}

export const SummaryCards = ({
  activeKey,
  groups,
  onSelect,
}: SummaryCardsProps) => (
  <Box
    display="grid"
    gridTemplateColumns={{
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(5, minmax(0, 1fr))',
    }}
    gap={1.25}
  >
    {groups.map((group) => {
      const active =
        activeKey === group.key || (!activeKey && group.key === 'total');

      return (
        <Tooltip key={group.key} title={group.tooltip} arrow>
          <Paper
            component={ButtonBase}
            onClick={() => onSelect?.(group.key)}
            sx={{
              bgcolor: active ? `${group.color}.light3` : 'background.paper',
              borderColor: active ? `${group.color}.main` : 'divider',
              borderRadius: 1.5,
              borderStyle: 'solid',
              borderWidth: 1,
              boxShadow: '0 8px 24px rgba(17, 24, 39, 0.05)',
              justifyContent: 'stretch',
              minHeight: 88,
              p: { xs: 1, md: 1.15 },
              textAlign: 'left',
              width: '100%',
              '&:hover': {
                bgcolor: `${group.color}.light3`,
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1.15} width="100%">
              <Box
                sx={{
                  bgcolor: active
                    ? 'background.paper'
                    : `${group.color}.light3`,
                  color: `${group.color}.main`,
                  borderRadius: '50%',
                  width: 38,
                  height: 38,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  '& .MuiSvgIcon-root': { fontSize: 20 },
                }}
              >
                {group.icon}
              </Box>
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                  sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.15 }}
                >
                  {group.label}
                </Typography>
                <Typography
                  fontWeight={800}
                  lineHeight={1.08}
                  sx={{ fontSize: 22, mt: 0.25 }}
                >
                  {group.count}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                  sx={{ fontSize: 12, lineHeight: 1.15, mt: 0.3 }}
                >
                  {formatMoney(group.amount)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Tooltip>
      );
    })}
  </Box>
);

export default SummaryCards;
